import {
  isBoolean,
  isString,
  isUndefinedOrNull,
  typeGuardGenerator,
  unionGuard,
} from '@metools/tcheck';

import {
  isStringArray,
  isStringOrUndefinedOrNull,
  isValidDateTimeString,
} from '@qsrsoft/myq-core';

/** @typedef {import('myq').ValidDateTimeString} ValidDateTimeString */

/**
 * @typedef {Object} MyQCalendarJSON
 * @property {string} id - The unique identifier of the calendar.
 * @property {string} orgId - The organization ID.
 * @property {string} title - The calendar title.
 * @property {string} color - The calendar color.
 * @property {boolean} active - Indicates if the calendar is active.
 * @property {string} createdTime - The timestamp when the calendar was created.
 * @property {string} createdByUserId - The ID of the calendar creator.
 * @property {string} lastEditedTime - The timestamp of the last edit.
 * @property {string} lastEditedByUserId - The ID of the last editor.
 * @property {Object} [remoteSubscriptions] - The remote subscriptions of the calendar.
 * @property {string[]} [roleAccessList] - The roles that are shared with the calendar.
 * @property {string[]} [locationAccessList] - The locations that are shared with the calendar.
 * @property {Object} [remoteSubscriptions] - The remote subscriptions of the calendar.
 * @property {boolean} [isCommsHubCalendar] - Indicates if this is a CommsHub calendar.
 * @property {string} [orgName] - The name of the org the calendar belongs to. Set on CommsHub calendars so an event can show which hub it came from.
 */

const isStringArrayOrUndefinedOrNull = unionGuard(
  isStringArray,
  isUndefinedOrNull,
);

const isBooleanOrUndefinedOrNull = unionGuard(isBoolean, isUndefinedOrNull);

const myqCalendarJSONCommon = {
  id: isString,
  orgId: isString,
  title: isString,
  color: isString,
  active: isBoolean,
  createdTime: isString,
  createdByUserId: isString,
  lastEditedTime: isString,
  lastEditedByUserId: isString,
  roleAccessList: isStringArrayOrUndefinedOrNull,
  locationAccessList: isStringArrayOrUndefinedOrNull,
  isCommsHubCalendar: isBooleanOrUndefinedOrNull,
  orgName: isStringOrUndefinedOrNull,
};

/** @type {import('@metools/tcheck').TypeGuard<MyQCalendarJSON>} */
const isMyQCalendarJSON = typeGuardGenerator(myqCalendarJSONCommon);

export class MyQCalendar {
  /** @type {string} */
  _id;
  /** @type {string} */
  _orgId;
  /** @type {string} */
  _title;
  /** @type {string} */
  _color;
  /** @type {boolean} */
  _active;
  /** @type {string} */
  _createdTime;
  /** @type {string} */
  _createdByUserId;
  /** @type {string} */
  _lastEditedTime;
  /** @type {string} */
  _lastEditedByUserId;
  /** @type {string[] | undefined} */
  _roleAccessList;
  /** @type {string[] | undefined} */
  _locationAccessList;
  /** @type {Object | undefined} */
  _remoteSubscriptions;
  /** @type {boolean | undefined} */
  _isCommsHubCalendar;
  /** @type {string | undefined} */
  _orgName;

  /**
   * @param {MyQCalendarJSON} calendar
   */
  constructor(calendar) {
    this._id = calendar.id;
    this._orgId = calendar.orgId;
    this._title = calendar.title;
    this._color = calendar.color;
    this._active = calendar.active;
    this._createdTime = calendar.createdTime;
    this._createdByUserId = calendar.createdByUserId;
    this._lastEditedTime = calendar.lastEditedTime;
    this._lastEditedByUserId = calendar.lastEditedByUserId;
    this._roleAccessList = calendar.roleAccessList ?? undefined;
    this._locationAccessList = calendar.locationAccessList ?? undefined;
    this._remoteSubscriptions = calendar.remoteSubscriptions ?? undefined;
    this._isCommsHubCalendar = calendar.isCommsHubCalendar ?? false;
    this._orgName = calendar.orgName ?? undefined;
  }

  get id() {
    return this._id;
  }
  get orgId() {
    return this._orgId;
  }
  get title() {
    return this._title;
  }
  get color() {
    return this._color;
  }
  get active() {
    return this._active;
  }
  get createdTime() {
    return this._createdTime;
  }
  get createdByUserId() {
    return this._createdByUserId;
  }
  get lastEditedTime() {
    return this._lastEditedTime;
  }
  get lastEditedByUserId() {
    return this._lastEditedByUserId;
  }
  get roleAccessList() {
    return this._roleAccessList;
  }
  get locationAccessList() {
    return this._locationAccessList;
  }
  get remoteSubscriptions() {
    return this._remoteSubscriptions;
  }
  get isCommsHubCalendar() {
    return this._isCommsHubCalendar;
  }
  get orgName() {
    return this._orgName;
  }
  /**
   *
   * @returns {MyQCalendarJSON}
   */
  toJSON() {
    return {
      id: this.id,
      orgId: this.orgId,
      title: this.title,
      color: this.color,
      active: this.active,
      createdTime: this.createdTime,
      createdByUserId: this.createdByUserId,
      lastEditedTime: this.lastEditedTime,
      lastEditedByUserId: this.lastEditedByUserId,
      roleAccessList: this.roleAccessList,
      locationAccessList: this.locationAccessList,
      remoteSubscriptions: this.remoteSubscriptions,
      isCommsHubCalendar: this.isCommsHubCalendar,
      orgName: this.orgName,
    };
  }

  static isMyQCalendarJSON = isMyQCalendarJSON;
}

/**
 * @typedef {object} RemoteSubscriptionCalendarJSON
 * @property {string} id - The ID of the subscription calendar.
 * @property {string} orgId - The ID of the organization.
 * @property {string} myqCalendarId - The ID of the MyQ calendar.
 * @property {string} url - The URL of the subscription calendar.
 * @property {string} calendarName - The name of the subscription calendar.
 * @property {string} color - The color of the subscription calendar.
 * @property {import('@qsrsoft/myq-core').ValidDateTimeString} addedTime - The time the subscription was added.
 * @property {import('@qsrsoft/myq-core').ValidDateTimeString} lastretrievalTime - The last time the subscription was retrieved.
 * @property {string[]} [roleAccessList] - The roles that have access to the subscription calendar.
 * @property {string[]} [locationAccessList] - The locations that have access to the subscription calendar.
 */

const isRemoteSubscriptionEventJSONCommon = {
  id: isString,
  orgId: isString,
  myqCalendarId: isString,
  url: isString,
  calendarName: isString,
  color: isString,
  addedTime: isValidDateTimeString,
  lastretrievalTime: isValidDateTimeString,
  roleAccessList: isStringArrayOrUndefinedOrNull,
  locationAccessList: isStringArrayOrUndefinedOrNull,
};

/** @type {import('@metools/tcheck').TypeGuard<RemoteSubscriptionCalendarJSON>} */
const isRemoteSubscriptionEventJSON = typeGuardGenerator(
  isRemoteSubscriptionEventJSONCommon,
);

export class RemoteSubscriptionCalendar {
  /** @type {string} */
  _id;
  /** @type {string} */
  _orgId;
  /** @type {string} */
  _myqCalendarId;
  /** @type {string} */
  _url;
  /** @type {string} */
  _calendarName;
  /** @type {string} */
  _color;
  /** @type {ValidDateTimeString} */
  _addedTime;
  /** @type {ValidDateTimeString} */
  _lastretrievalTime;
  /** @type {string[] | undefined} */
  _roleAccessList;
  /** @type {string[] | undefined} */
  _locationAccessList;

  /**
   * @param {RemoteSubscriptionCalendarJSON} input
   */
  constructor(input) {
    this._id = input.id;
    this._orgId = input.orgId;
    this._myqCalendarId = input.myqCalendarId;
    this._url = input.url;
    this._calendarName = input.calendarName;
    this._color = input.color;
    this._addedTime = input.addedTime;
    this._lastretrievalTime = input.lastretrievalTime;
    this._roleAccessList = input.roleAccessList ?? undefined;
    this._locationAccessList = input.locationAccessList ?? undefined;
  }

  get id() {
    return this._id;
  }
  get orgId() {
    return this._orgId;
  }
  get myqCalendarId() {
    return this._myqCalendarId;
  }
  get url() {
    return this._url;
  }
  get calendarName() {
    return this._calendarName;
  }
  get color() {
    return this._color;
  }
  get addedTime() {
    return this._addedTime;
  }
  get lastretrievalTime() {
    return this._lastretrievalTime;
  }
  get roleAccessList() {
    return this._roleAccessList;
  }
  get locationAccessList() {
    return this._locationAccessList;
  }

  /**
   * @returns {RemoteSubscriptionCalendarJSON}
   */
  toJSON() {
    return {
      id: this.id,
      orgId: this.orgId,
      myqCalendarId: this.myqCalendarId,
      url: this.url,
      calendarName: this.calendarName,
      color: this.color,
      addedTime: this.addedTime,
      lastretrievalTime: this.lastretrievalTime,
      roleAccessList: this.roleAccessList,
      locationAccessList: this.locationAccessList,
    };
  }

  static isRemoteSubscriptionEventJSON = isRemoteSubscriptionEventJSON;
}

/**
 * @typedef {Object} Location
 * @property {string} locationId
 * @property {string} name
 * @property {string} nameValue
 * @property {string} value
 * @property {boolean} isActive
 * @property {string} locationStatus
 */
